/**
 * Fatiado de ReviewPanel.tsx (#489 B, commit 1 — extração PURA, sem mudança
 * de comportamento): o ESTADO e a LÓGICA do cartão de moeda da revisão —
 * a apresentação fica em ReviewLocaleCurrency.tsx (como o idioma já fazia).
 */
import { useEffect, useState } from 'react'
import type { QueryClient } from '@tanstack/react-query'
import { importsApi, type MenuImportView } from '../../lib/api.js'
import {
  currencyFromMenuLocale,
  currencyNeedsConfirmation,
} from '@self-restaurant/menu-import-core'
import { importErrorMessageFor, isKeyedImportError } from '../../lib/import-errors.js'

type Translate = (key: string, vars?: Record<string, string>) => string

export function useReviewCurrency({
  view,
  locale,
  t,
  qc,
}: {
  view: MenuImportView
  /** idioma ESCOLHIDO no seletor — o commit usa o locale do payload, não o imp.locale da vista */
  locale: string
  t: Translate
  qc: QueryClient
}) {
  // #299 crit. 7: moeda EDITÁVEL — a escolha SÓ vale quando o servidor
  // responde OK; em erro desfaz e «Importar» segue bloqueado (QA 5)
  const [currency, setCurrency] = useState(() => view.currency ?? '')
  const [currencyConfirmed, setCurrencyConfirmed] = useState(() => view.currency_confirmed === true)
  const [currencyPending, setCurrencyPending] = useState(false)
  // M1/B2: confirmação OK → foca «Importar» quando o botão ficar ativo
  const [commitFocusPending, setCommitFocusPending] = useState(false)
  // a vista muda (refetch após o POST, revisão aplicada…) → ressincroniza
  useEffect(() => {
    setCurrency(view.currency ?? '')
    setCurrencyConfirmed(view.currency_confirmed === true)
  }, [view.currency, view.currency_confirmed])
  // (UX) 422 por chave i18n na língua do painel — lib/import-errors.ts
  const importErrorMessage = (e: unknown): string =>
    importErrorMessageFor(t, view.restaurant_currency, e)
  // 422 de moeda no commit (corrida): reabre a faixa e
  // traz o foco ao cartão; o estado «confirmado» do cliente era falso
  const [currencyMismatchAlert, setCurrencyMismatchAlert] = useState(false)
  // #486 (item 2): erro com chave i18n INLINE no cartão (role="alert")
  const [cardError, setCardError] = useState<string | null>(null)
  const focusCurrencyCard = () => document.getElementById('import-currency-card')?.focus()
  const pickCurrency = (code: string): Promise<void> => {
    if (currencyPending) return Promise.resolve()
    setCurrency(code)
    setCurrencyPending(true)
    setCardError(null)
    return importsApi
      .confirmCurrency(view.id, code)
      .then(() => {
        setCurrencyConfirmed(true)
        setCurrencyMismatchAlert(false) // M1/B3: a faixa sai
        setCommitFocusPending(true) // M1/B2: foco em «Importar» via useEffect
        void qc.invalidateQueries({ queryKey: ['menu-import', view.id] })
      })
      .catch((e: Error) => {
        // falhou → desfaz: sem resposta OK a moeda não conta como confirmada
        setCurrency(view.currency ?? '')
        setCurrencyConfirmed(view.currency_confirmed === true)
        // #486: erro INLINE no cartão com role="alert" + foco no cartão —
        // o leitor de voz sabe onde resolver (antes: toast não anunciado)
        setCardError(importErrorMessage(e))
        focusCurrencyCard()
      })
      .finally(() => setCurrencyPending(false))
  }
  // ramos de moeda/idioma do commit (I2 §3 + #486): true = tratado (faixa
  // reaberta ou erro inline) — o toast genérico fica no ReviewPanel
  const handleCommitCurrencyError = (e: Error): boolean => {
    // corrida: reabre a faixa com role="alert" e foca o cartão (I2 §3)
    if ((e as { code?: string }).code === 'CURRENCY_CONFIRMATION_REQUIRED') {
      setCurrencyConfirmed(false)
      setCurrencyMismatchAlert(true)
      qc.invalidateQueries({ queryKey: ['menu-import', view.id] })
      focusCurrencyCard()
      return true
    }
    // #486 (item 2): 422 com chave i18n (idioma/moeda) → INLINE no cartão
    // com role="alert" + foco; o resto segue em toast (anunciado pelo
    // Toaster, que tem role="status"/aria-live desde o #474)
    if (isKeyedImportError(e)) {
      setCardError(importErrorMessage(e))
      focusCurrencyCard()
      return true
    }
    return false
  }
  // crit. 7: moeda divergente trava o commit até o servidor confirmar a
  // escolha do dono (pedido pendente ou falhado também bloqueiam).
  // (g) revisão UX: recalculado com o idioma ESCOLHIDO no seletor — o commit
  // usa o locale do payload, não o imp.locale da vista; assim o admin nunca
  // deixa importar o que o servidor vai recusar com 422
  const restaurantMissing = !view.restaurant_currency // spec-arq §3: 422 no servidor
  const currencyNeedsConfirm =
    (currencyNeedsConfirmation({
      detected: view.currency_detected ? (view.currency ?? null) : null,
      fromLocaleRegion: currencyFromMenuLocale(locale),
      restaurantCurrency: view.restaurant_currency ?? null,
      confirmedAt: view.currency_confirmed || currencyConfirmed ? 1 : null,
    }) &&
      !currencyConfirmed) ||
    restaurantMissing ||
    currencyPending ||
    currencyMismatchAlert
  // M1/8: moeda que se mostra JUNTO aos preços — a que será GRAVADA (do
  // restaurante; sem restaurante, a lida no Menu)
  const displayCurrency = view.restaurant_currency ?? view.currency ?? undefined
  return {
    currency,
    currencyConfirmed,
    currencyPending,
    currencyMismatchAlert,
    cardError,
    setCardError,
    restaurantMissing,
    currencyNeedsConfirm,
    commitFocusPending,
    setCommitFocusPending,
    pickCurrency,
    focusCurrencyCard,
    importErrorMessage,
    handleCommitCurrencyError,
    displayCurrency,
  }
}
